<!-- BarcodeScanner.vue -->
<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { BrowserMultiFormatReader, Result } from '@zxing/library'

interface Props {
  allowManual?: boolean
  autoStart?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  allowManual: true,
  autoStart: false
})

const emit = defineEmits<{
  (e: 'scanned', code: string): void
  (e: 'error', error: Error): void
}>()

const video = ref<HTMLVideoElement | null>(null)
const videoContainer = ref<HTMLDivElement | null>(null)
const isScanning = ref(false)
const hasCamera = ref(false)
const hasTorch = ref(false)
const feedbackMessage = ref('')
const feedbackType = ref('')
const manualCode = ref('')

const codeReader = new BrowserMultiFormatReader()

const startScanner = async () => {
  try {
    if (!video.value) return
    isScanning.value = true
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
    video.value.srcObject = stream
    
    codeReader.decodeFromVideoDevice(null, video.value as unknown as string, (result: Result | null, error?: Error) => {
      if (result) {
        handleScan(result.getText())
      }
      if (error && !(error instanceof TypeError)) {
        // Ignore TypeError as it's thrown when scanning is stopped
        handleError(error)
      }
    })

    // Check if torch is available
    const track = stream.getVideoTracks()[0]
    hasTorch.value = 'torch' in track.getCapabilities()
  } catch (error) {
    handleError(error as Error)
  }
}

const stopScanner = () => {
  isScanning.value = false
  if (video.value?.srcObject) {
    const stream = video.value.srcObject as MediaStream
    stream.getTracks().forEach(track => track.stop())
  }
  codeReader.reset()
}

const toggleScanner = () => {
  if (isScanning.value) {
    stopScanner()
  } else {
    startScanner()
  }
}

const toggleTorch = async () => {
  if (!video.value?.srcObject) return
  const stream = video.value.srcObject as MediaStream
  const track = stream.getVideoTracks()[0]
  const capabilities = track.getCapabilities()
  
  if ('torch' in capabilities) {
    const constraints = track.getConstraints()
    const torchState = !constraints.advanced?.find(c => 'torch' in c)?.torch
    await track.applyConstraints({
      advanced: [{ torch: torchState } as any]
    })
  }
}

const handleScan = (code: string) => {
  feedbackType.value = 'success'
  feedbackMessage.value = 'Code scanned successfully'
  emit('scanned', code)
}

const handleError = (error: Error) => {
  feedbackType.value = 'error'
  feedbackMessage.value = error.message
  emit('error', error)
}

const handleManualInput = () => {
  if (manualCode.value) {
    handleScan(manualCode.value)
    manualCode.value = ''
  }
}

onMounted(async () => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    hasCamera.value = devices.some(device => device.kind === 'videoinput')
    
    if (props.autoStart && hasCamera.value) {
      startScanner()
    }
  } catch (error) {
    handleError(error as Error)
  }
})

onBeforeUnmount(() => {
  stopScanner()
})
</script>

<template>
  <div class="scanner-container">
    <div class="video-container" ref="videoContainer">
      <video ref="video" class="scanner-video" autoplay playsinline></video>
      <div class="scanner-overlay">
        <div class="scanner-target"></div>
      </div>
    </div>
    
    <div class="scanner-controls">
      <button 
        @click="toggleScanner" 
        :disabled="!hasCamera"
        class="control-button"
      >
        {{ isScanning ? 'Stop' : 'Start' }} Scanner
      </button>
      
      <button 
        v-if="hasTorch"
        @click="toggleTorch"
        class="control-button"
      >
        Toggle Torch
      </button>
    </div>

    <div 
      v-if="allowManual"
      class="manual-input"
    >
      <input 
        type="text" 
        v-model="manualCode"
        placeholder="Enter barcode manually"
        @keyup.enter="handleManualInput"
        class="manual-input-field"
      />
      <button 
        @click="handleManualInput"
        class="manual-submit"
      >
        Submit
      </button>
    </div>

    <div 
      v-if="feedbackMessage"
      class="scanner-feedback"
      :class="feedbackType"
    >
      {{ feedbackMessage }}
    </div>
  </div>
</template>

<style scoped>
.scanner-container {
  @apply flex flex-col items-center gap-4 w-full max-w-lg mx-auto;
}

.video-container {
  @apply relative w-full aspect-video bg-gray-900 rounded-lg overflow-hidden;
}

.scanner-video {
  @apply w-full h-full object-cover;
}

.scanner-overlay {
  @apply absolute inset-0 flex items-center justify-center;
  background: rgba(0, 0, 0, 0.2);
}

.scanner-target {
  @apply w-64 h-64 border-2 border-white rounded-lg;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
}

.scanner-controls {
  @apply flex gap-2;
}

.control-button {
  @apply px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
         disabled:bg-gray-400 disabled:cursor-not-allowed;
}

.manual-input {
  @apply flex gap-2 w-full;
}

.manual-input-field {
  @apply flex-1 px-4 py-2 border border-gray-300 rounded-lg;
}

.manual-submit {
  @apply px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700;
}

.scanner-feedback {
  @apply p-4 rounded-lg text-center w-full;
}

.scanner-feedback.success {
  @apply bg-green-100 text-green-800;
}

.scanner-feedback.error {
  @apply bg-red-100 text-red-800;
}
</style>